// src/components/comments/CommentCard.jsx
//
// A single comment "module" from the collaborator design: commenter identity,
// its tags, the comment body, an always-open reply box, and the overflow menu
// (Notify / Discard / Edit / Add tags / Mark as Unread / Report).
//
// Notify and Discard used to sit on the card as buttons. They are in the menu
// now, alongside the other actions, because they are no longer things everyone
// can do -- see the permission block below.
//
// Rendered on the white cards inside the purple comment sidebar, so the palette
// here is the app's ink-on-white rather than the mock's cream. Actions are
// optimistic where the ticket implies immediacy (discard) and confirmed where
// they leave the app (notify).
import React, { useState } from 'react';
import {
  Box, Typography, Avatar, Menu, MenuItem, Divider, Chip, TextField,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import { isCriticalComment } from '../../utils/commentHighlights';
import ReplyComposer from './ReplyComposer';

const GOLD = '#D4AF37';
const GOLD_HOVER = '#F5D76E';
const INK = '#1A1A1A';
const CRITICAL = '#D85A30';

// Deterministic 2-letter initials, matching EditorRightRail's treatment.
const initialsOf = (u) => {
  const name = u?.username || u?.name || u?.email || '?';
  const parts = String(name).trim().split(/\s+/);
  const a = parts[0]?.[0] || '';
  const b = parts.length > 1 ? parts[parts.length - 1][0] : (parts[0]?.[1] || '');
  return (a + b).toUpperCase();
};

const displayName = (u) =>
  u?.username || u?.name || (u?.email ? String(u.email).split('@')[0] : 'User');

// Plain-Box button. The comment sidebar's CSS restyles MUI buttons, and this
// keeps the two actions looking like the design rather than inheriting it.
function CardButton({ variant = 'ghost', onClick, disabled, children, title }) {
  const primary = variant === 'primary';
  return (
    <Box
      component="button"
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      sx={{
        font: 'inherit', fontSize: 12, fontWeight: 700, lineHeight: 1,
        px: 2.25, py: 1, borderRadius: '6px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        color: INK,
        backgroundColor: primary ? GOLD : '#FFFFFF',
        border: primary ? `1px solid ${GOLD}` : '1px solid rgba(0,0,0,0.18)',
        '&:hover': {
          backgroundColor: disabled ? undefined : (primary ? GOLD_HOVER : 'rgba(0,0,0,0.04)'),
        },
      }}
    >
      {children}
    </Box>
  );
}

export default function CommentCard({
  comment,
  currentUserId,
  // Whether the viewer is an admin, and whether they own the manuscript this
  // comment sits on. Both default to false, which only ever hides an action --
  // the server is the gate, so a false here degrades rather than exposes.
  isAdmin = false,
  isItemOwner = false,
  onNotify,
  onDiscard,
  onEdit,
  onReply,
  onReport,
  onAddTags,
  onMarkUnread,
  busy = false,
  replyBusy = false,
}) {
  const [menuEl, setMenuEl] = useState(null);
  const [mode, setMode] = useState(null);   // 'edit' | 'tags' | null
  const [draft, setDraft] = useState('');

  const c = comment || {};
  const id = c.id || c._id;
  const author = c.user || {};
  // Both sides must be non-empty. Without the Boolean guard the two `|| ''`
  // fallbacks collapse to '' === '' and an unidentified viewer owns any comment
  // carrying no author id -- which offered the whole gated menu to everybody.
  // Reachable, not theoretical: BookReader resolves its user to null on the
  // first render, and comments written before the `user.id` field lack one.
  const isOwner =
    Boolean(currentUserId) &&
    String(author.id || c.user_id || '') === String(currentUserId);
  const critical = isCriticalComment(c);
  const tags = Array.isArray(c.tags) ? c.tags : [];

  // These three mirror backend/routes/comments.py exactly, and they are three
  // different sets rather than one -- copying any one of them onto the others
  // would either hide something the server allows or show something it refuses.
  //
  //   notify   the comment's author only. It sends in their name.
  //   discard  the author, or an admin moderating.
  //   tags     the author, or the manuscript's author triaging their feedback.
  //
  // Hiding is the whole UI contribution here: the server refuses these
  // independently, so a stale tab cannot act through a button it should not
  // have. Reply and Report stay open to every reader, as they were.
  const canNotify = isOwner;
  const canDiscard = isOwner || isAdmin;
  const canTag = isOwner || isItemOwner;
  const hasOwnerActions = canNotify || canDiscard || canTag;

  const closeMenu = () => setMenuEl(null);

  const startMode = (next, seed = '') => {
    closeMenu();
    setMode(next);
    setDraft(seed);
  };

  const submitMode = () => {
    const value = draft.trim();
    if (!value) { setMode(null); return; }
    if (mode === 'edit') onEdit?.(id, value);
    else if (mode === 'tags') {
      // Comma-separated, de-duplicated against what's already there.
      const next = value.split(',').map((t) => t.trim()).filter(Boolean);
      const merged = [...tags];
      next.forEach((t) => {
        if (!merged.some((x) => x.toLowerCase() === t.toLowerCase())) merged.push(t);
      });
      onAddTags?.(id, merged);
    }
    setMode(null);
    setDraft('');
  };

  return (
    <Box
      className="comment-card"
      sx={{
        borderRadius: '10px', p: 2, mb: 1.5,
        // A critical revision is legible as such before it's opened.
        borderLeft: critical ? `3px solid ${CRITICAL}` : undefined,
      }}
    >
      {/* Identity + overflow menu */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Avatar sx={{ width: 26, height: 26, fontSize: 10, fontWeight: 700, bgcolor: '#6B4FA0', color: '#fff' }}>
            {initialsOf(author)}
          </Avatar>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayName(author)}
          </Typography>
          {!c.read && (
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: GOLD, flexShrink: 0 }} title="Unread" />
          )}
        </Box>
        <Box
          component="span"
          title="More"
          onClick={(e) => setMenuEl(e.currentTarget)}
          sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 24, height: 24, borderRadius: '4px', cursor: 'pointer',
            color: '#6B6B6B', flexShrink: 0,
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
          }}
        >
          <MoreHorizIcon sx={{ fontSize: 18 }} />
        </Box>
      </Box>

      {/* Tags */}
      {tags.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
          {tags.map((t) => {
            const isCrit = /critical/i.test(t);
            return (
              <Chip
                key={t}
                label={t}
                size="small"
                sx={{
                  height: 20, fontSize: 10, fontWeight: 700,
                  backgroundColor: isCrit ? INK : '#EDE7F6',
                  color: isCrit ? '#FFFFFF' : '#3C1F63',
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Body — or the inline editor when editing */}
      {mode === 'edit' ? (
        <TextField
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          size="small"
          autoFocus
          sx={{ mb: 1.25, '& .MuiInputBase-input': { fontSize: 13, color: INK } }}
        />
      ) : (
        <Typography sx={{ fontSize: 13, color: INK, mb: 1.5, whiteSpace: 'pre-wrap' }}>
          {c.text}
          {c.editedAt && (
            <Typography component="span" sx={{ fontSize: 11, color: '#6B6B6B', ml: 0.75 }}>
              (edited)
            </Typography>
          )}
        </Typography>
      )}

      {/* Tag composer. Reply no longer comes through here — it has its own
          always-open box below, so there is nothing to switch mode into. */}
      {mode === 'tags' && (
        <TextField
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add tags, comma separated"
          fullWidth
          size="small"
          autoFocus
          sx={{ mb: 1.25, '& .MuiInputBase-input': { fontSize: 13, color: INK } }}
        />
      )}

      {/* Save/Cancel belong to the edit and tag composers only. With Notify
          and Discard moved into the menu there is no resting state for this
          row, so it disappears entirely when no composer is open. */}
      {mode && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <CardButton variant="primary" onClick={submitMode} disabled={busy}>
            {mode === 'edit' ? 'Save' : 'Add tags'}
          </CardButton>
          <CardButton onClick={() => { setMode(null); setDraft(''); }}>Cancel</CardButton>
        </Box>
      )}

      {/* Reply, always open. It replaces the menu item that used to swap the
          card into a reply mode: one click fewer, and the affordance is
          visible without opening anything. Hidden only while another composer
          has the card, so two text boxes are never open at once. */}
      {!mode && (
        <ReplyComposer
          busy={replyBusy}
          onSubmit={(text) => onReply?.(id, text)}
        />
      )}

      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={closeMenu}>
        {/* Every item down to the divider is gated, each on its own flag — see
            the permission block above. The backend enforces all of them, so a
            stale tab cannot act through an item it should not have been shown. */}
        {canNotify && (
          <MenuItem onClick={() => { closeMenu(); onNotify?.(c); }} disabled={busy}>
            Notify
          </MenuItem>
        )}
        {canDiscard && (
          <MenuItem onClick={() => { closeMenu(); onDiscard?.(id); }} disabled={busy}>
            Discard
          </MenuItem>
        )}
        {isOwner && (
          <MenuItem onClick={() => startMode('edit', c.text || '')}>Edit</MenuItem>
        )}
        {canTag && (
          <MenuItem onClick={() => startMode('tags')}>Add tags</MenuItem>
        )}
        {hasOwnerActions && <Divider />}
        <MenuItem onClick={() => { closeMenu(); onMarkUnread?.(id); }}>
          Mark as Unread
        </MenuItem>
        <MenuItem
          onClick={() => { closeMenu(); onReport?.(id); }}
          sx={{ color: CRITICAL }}
        >
          Report to ReVisionari
        </MenuItem>
      </Menu>
    </Box>
  );
}
